import { useTranslation } from "react-i18next"
import useStore from "../../../store"
import DatePicker from "react-datepicker"
import { useState, useEffect } from "react"
import UploadUnitFile from "./UploadUnitFile"
import { HappyButton } from "../../common/Buttons"
import { Input } from '@chakra-ui/react'
import { FiAlertCircle } from "react-icons/fi"
import { colors } from "../../../utilities/colors"
import saleOptions, { employeeSaleOptions } from "../../../constants/saleTypeOptions"
import SaleTypeSelect from "./SaleTypeSelect"
import FinancialCompanyInput from "./FinancialCompanyInput"
import useUserType from "../../../hooks/useUserType"

export default function MarkSoldForm({ unit }) {
  const { t } = useTranslation()
  const { isEmployee } = useUserType()
  const {
    markUnitSold,
    createUnitFile,
    setErrMessage
  } = useStore()

  const soldOn = unit?.attributes["sold-on"]
  const defaultSaleType = [2] //Cash

  const [soldOnDate, setSoldOnDate] = useState(new Date())
  const [soldTo, setSoldTo] = useState("")
  const [saleType, setSaleType] = useState(defaultSaleType)
  const [fundingSource, setFundingSource] = useState(unit?.attributes["funding-source"] ?? "")
  const [isSoldToError, setIsSoldToError] = useState(false)
  const [bosFiles, setBosFiles] = useState([])

  const isFinanceSale = saleType[0] === 3 //Finance

  useEffect(() => {
    if (bosFiles.length === 0) {
      const employeeValues = [2, 4]
      if (isEmployee && employeeValues.includes(saleType[0])) return
      setSaleType([2]) //Cash
    }
  }, [bosFiles])

  const handleDateChange = (date) => {
    setSoldOnDate(date.toLocaleDateString('en-ZA'))
  }

  const handleSubmit = async () => {
    if (!soldTo) {
      setErrMessage("Sold To is a required field")
      setIsSoldToError(true)
      return
    } else {
      setIsSoldToError(false)
    }

    try {
      if (soldOnDate) {
        await markUnitSold(unit, soldOnDate, soldTo, saleType[0], isFinanceSale ? fundingSource : "")
      }

      for (const file of bosFiles) {
        const formData = new FormData()
        formData.append('unit_file[ufile]', file)
        formData.append('unit_file[unit_id]', unit.id)
        formData.append('unit_file[name]', "Bill Of Sale (BOS)")
        formData.append('unit_file[description]', "Bill Of Sale")

        await createUnitFile(formData, unit.id)
      }

      setBosFiles([])
    } catch (error) {
      console.log("Error submitting form:", error)
    }
  };

  return(
    <div>
      {!soldOn &&
        <>
          <div className="flex items-center mb-2">
            <label className="text-sm text-grey-300 mr-2">{t("Date Sold")}</label>
            <DatePicker
              selected={soldOnDate}
              onChange={(date) => handleDateChange(date)}
              className="px-4 w-32 ml-1 bg-grey-900 border-2 border-grey-700 focus:border-grey-300 focus:ring-0 rounded"
              required
            />
          </div>

          <div className="flex items-center mb-2">
            <label className="text-sm text-grey-300 mr-2">{t("Sold To")}</label>
            <Input
              value={soldTo}
              onChange={(e) => setSoldTo(e.target.value)}
              className="px-4 w-32 ml-1 bg-grey-900 rounded"
              borderWidth={2}
              borderColor={isSoldToError ? colors.fpxRed : colors.grey700}
              _focus={{ borderColor: isSoldToError ? colors.fpxRed : colors.grey300, ringColor: "transparent" }}
              required
            />
          </div>

          <div className="flex items-center mb-2">
            <UploadUnitFile
              label={t("Bill of Sale")}
              files={bosFiles}
              setFiles={setBosFiles}
            />
          </div>

          {bosFiles.length === 0 && !isEmployee &&
            <div className="flex items-center mb-2 text-sm">
              <FiAlertCircle className="mr-2"/>
              <div className="text-grey-300">Add Bill of Sale to update sale type</div>
            </div>
          }

          <div className="flex items-center mb-2">
            <label className="text-sm text-grey-300 mr-2">{t("Sale Type")}</label>
            {(bosFiles.length > 0 || isEmployee) ?
              <SaleTypeSelect
                collection={bosFiles.length > 0 ? saleOptions : employeeSaleOptions}
                value={saleType}
                onChange={setSaleType}
              />
              :
              <div className="text-grey-100 flex items-center">
                {t("Cash")}
              </div>
            }
          </div>

          {isFinanceSale &&
            <div className="flex items-center mb-2">
              <label className="text-sm text-grey-300 mr-2">{t("Financial Company")}</label>
              <FinancialCompanyInput value={fundingSource} onChange={setFundingSource} />
            </div>
          }
        </>
      }

      <HappyButton
        dataTestId="mark-sold-button"
        text={t('Mark Unit Sold')}
        onClick={handleSubmit}
      />
    </div>
  )
}
